import React, { useMemo, useState } from "react";
import { Box, Button, Chip, CircularProgress, TextField, Tooltip, Typography } from "@mui/material";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import { answerAuditCheck, unwrapPost } from "../../../../helpers/auditHub_helper";
import { AH_BORDER, AH_INPUT_BORDER, AH_MUTED, AH_SUBTLE, AH_TEXT, AH_TRACK, AH_WHITE } from "../../AuditHub/theme";

const ANSWERS = [
  { value: "PASS", label: "Pass", color: "#16a34a", tint: "#f0fdf4", border: "#bbf7d0", Icon: CheckCircleOutlineRoundedIcon },
  { value: "FAIL", label: "Fail", color: "#dc2626", tint: "#fef2f2", border: "#fecaca", Icon: HighlightOffRoundedIcon },
  { value: "NOT_APPLICABLE", label: "Not Applicable", color: "#6b7280", tint: "#f9fafb", border: "#e5e7eb", Icon: BlockRoundedIcon },
];

const NOTE_MAX = 4000;

const sectionLabelSx = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: AH_MUTED,
};

const cardSx = {
  border: `1px solid ${AH_BORDER}`,
  borderRadius: "10px",
  px: 1.5,
  py: 1.25,
  backgroundColor: AH_WHITE,
};

const sequenceSx = {
  width: 24,
  height: 24,
  borderRadius: "6px",
  backgroundColor: AH_TRACK,
  color: AH_SUBTLE,
  fontSize: 11,
  fontWeight: 700,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  mt: 0.125,
};

const answerChipSx = (item, selected) => ({
  height: 28,
  borderRadius: "8px",
  fontSize: "0.75rem",
  fontWeight: 600,
  border: `1px solid ${selected ? item.color : item.border}`,
  backgroundColor: selected ? item.color : AH_WHITE,
  color: selected ? AH_WHITE : item.color,
  "& .MuiChip-icon": { color: "inherit", fontSize: 15, ml: 0.75 },
  "&.MuiChip-clickable:hover, &.Mui-focusVisible": {
    backgroundColor: selected ? item.color : item.tint,
  },
  "&.Mui-disabled": {
    opacity: 1,
    color: "#94a3b8",
    borderColor: AH_INPUT_BORDER,
    backgroundColor: "#f8fafc",
  },
});

const noteFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.8125rem",
    backgroundColor: AH_WHITE,
    "& fieldset": { borderColor: AH_INPUT_BORDER },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: AH_TEXT, borderWidth: "1px" },
  },
  "& .MuiInputBase-input": { fontSize: "0.8125rem", color: AH_TEXT },
};

const noteButtonSx = {
  textTransform: "none",
  fontSize: "0.75rem",
  fontWeight: 600,
  minWidth: 0,
  height: 28,
  px: 1.25,
  borderRadius: "6px",
  boxShadow: "none",
  "&:hover": { boxShadow: "none" },
};

const ControlChecks = ({
  auditId,
  auditControlId,
  checks,
  answeredCount,
  questionCount,
  locked,
  disabled,
  nameFor,
  formatWhen,
  onAnswered,
}) => {
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState(null);
  const [drafts, setDrafts] = useState({});

  const ordered = useMemo(
    () => [...(Array.isArray(checks) ? checks : [])].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)),
    [checks]
  );

  if (!ordered.length) return null;

  const inactive = locked || disabled || Boolean(savingId);

  const save = async (check, body, { clearDraft = false } = {}) => {
    if (inactive) return;
    const id = check.auditControlCheckId;
    setSavingId(id);
    setError(null);
    try {
      const result = unwrapPost(await answerAuditCheck(auditId, auditControlId, id, body));
      if (clearDraft) {
        setDrafts((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }
      onAnswered(result);
    } catch (err) {
      setError(err?.message || "Unable to save this answer.");
    } finally {
      setSavingId(null);
    }
  };

  const onPick = (check, value) => {
    if (check.answer === value) {
      save(check, { answer: null, note: null }, { clearDraft: true });
      return;
    }
    save(check, { answer: value, note: check.note ?? null });
  };

  const total = Number.isFinite(questionCount) ? questionCount : ordered.length;
  const answered = Number.isFinite(answeredCount) ? answeredCount : ordered.filter((check) => check.answer).length;

  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, mb: 1.25 }}>
        <Typography sx={sectionLabelSx}>Questions</Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: answered === total ? "#16a34a" : AH_SUBTLE }}>
          {answered} of {total} answered
        </Typography>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {ordered.map((check) => {
          const id = check.auditControlCheckId;
          const saving = savingId === id;
          const savedNote = check.note || "";
          const draft = drafts[id];
          const noteValue = draft ?? savedNote;
          const noteDirty = draft !== undefined && draft.trim() !== savedNote;
          const answeredMeta = check.answeredBy
            ? [`Answered by ${nameFor(check.answeredBy)}`, formatWhen(check.answeredAt)].filter(Boolean).join(" · ")
            : "";
          return (
            <Box key={id} sx={cardSx}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
                <Box sx={sequenceSx} aria-hidden="true">
                  {check.sequence}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: AH_TEXT, lineHeight: 1.5, overflowWrap: "anywhere" }}>
                    {check.question}
                  </Typography>
                  {check.guidance ? (
                    <Typography sx={{ fontSize: 12, color: AH_MUTED, mt: 0.25, lineHeight: 1.5, overflowWrap: "anywhere" }}>
                      {check.guidance}
                    </Typography>
                  ) : null}
                  {check.evidenceHint ? (
                    <Typography sx={{ fontSize: 12, color: AH_MUTED, mt: 0.25, lineHeight: 1.5, overflowWrap: "anywhere" }}>
                      <Box component="span" sx={{ fontWeight: 600, color: AH_SUBTLE }}>
                        Evidence:
                      </Box>{" "}
                      {check.evidenceHint}
                    </Typography>
                  ) : null}

                  <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mt: 1 }}>
                    <Box
                      role="radiogroup"
                      aria-label={`Answer for question ${check.sequence}`}
                      sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}
                    >
                      {ANSWERS.map((item) => {
                        const selected = check.answer === item.value;
                        const enabled = !inactive;
                        const { Icon } = item;
                        const chip = (
                          <Chip
                            key={item.value}
                            role="radio"
                            aria-checked={selected}
                            variant="outlined"
                            label={item.label}
                            icon={<Icon />}
                            disabled={!selected && !enabled}
                            onClick={enabled ? () => onPick(check, item.value) : undefined}
                            sx={answerChipSx(item, selected)}
                          />
                        );
                        return selected && enabled ? (
                          <Tooltip key={item.value} title="Click again to clear this answer">
                            {chip}
                          </Tooltip>
                        ) : (
                          chip
                        );
                      })}
                    </Box>
                    {saving ? <CircularProgress size={14} sx={{ color: AH_MUTED }} /> : null}
                    {answeredMeta ? (
                      <Typography sx={{ fontSize: 12, color: AH_MUTED, ml: { sm: "auto" } }}>{answeredMeta}</Typography>
                    ) : null}
                  </Box>

                  {check.answer && locked && savedNote ? (
                    <Typography sx={{ fontSize: 12.5, color: "#374151", mt: 1, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                      <Box component="span" sx={{ fontWeight: 600, color: AH_SUBTLE }}>
                        Note:
                      </Box>{" "}
                      {savedNote}
                    </Typography>
                  ) : null}

                  {check.answer && !locked ? (
                    <Box sx={{ mt: 1 }}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={1}
                        maxRows={4}
                        size="small"
                        placeholder="Add a note (optional)"
                        value={noteValue}
                        disabled={disabled || saving}
                        onChange={(event) => setDrafts((prev) => ({ ...prev, [id]: event.target.value }))}
                        inputProps={{ maxLength: NOTE_MAX, "aria-label": `Note for question ${check.sequence}` }}
                        sx={noteFieldSx}
                      />
                      {noteDirty ? (
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.75, mt: 0.75 }}>
                          <Button
                            size="small"
                            disabled={saving}
                            onClick={() =>
                              setDrafts((prev) => {
                                const next = { ...prev };
                                delete next[id];
                                return next;
                              })
                            }
                            sx={{ ...noteButtonSx, color: AH_SUBTLE, "&:hover": { backgroundColor: "#f1f5f9", boxShadow: "none" } }}
                          >
                            Discard
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            disableElevation
                            disabled={inactive}
                            onClick={() =>
                              save(check, { answer: check.answer, note: draft.trim() || null }, { clearDraft: true })
                            }
                            sx={{
                              ...noteButtonSx,
                              backgroundColor: "#1976d2",
                              color: AH_WHITE,
                              "&:hover": { backgroundColor: "#1565c0", boxShadow: "none" },
                            }}
                          >
                            Save note
                          </Button>
                        </Box>
                      ) : null}
                    </Box>
                  ) : null}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
      {error ? (
        <Typography role="alert" sx={{ fontSize: 13, color: "#b91c1c", mt: 1 }}>
          {error}
        </Typography>
      ) : null}
    </Box>
  );
};

export default ControlChecks;
